import React, { FC, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { t } from 'ttag';

import { RemindPassFields } from './types';

import Alert from '../../ui/Alert/Alert';
import BackButton from '../../ui/BackButton/BackButton';
import Button from '../../ui/button/button';
import OutlinedInput from '../../ui/input/OutlinedInput/OutlinedInput';

import { EMAIL_MAX_LENGTH } from '../../../lib/constants';
import { getLocationQuery, stringifyLocationQuery } from '../../../lib/url';
import { getLocalStorageItem } from '../../../lib/webStorage';
import auth from '../../../services/auth';
import FlexBox from '../../FlexBox/FlexBox';
import { LS_KEY_AUTH_INITIAL_FORM } from '../../auth/constants';
import PageTitle from '../../pageTitle/pageTitle';
import { remindPassResolver } from './validation';

import partnersLogo from '../../../images/auth/partners-mobile.png';

import css from './NewRemindPassForm.css';

const NewRemindPassForm: FC = () => {
    const [emailForLetter, setEmailForLetter] = useState<string>('');
    const [commonErrorMessage, setCommonErrorMessage] = useState<string>('');

    const history = useHistory();
    const searchQuery = getLocationQuery(history.location);

    const initialFormData = getLocalStorageItem(LS_KEY_AUTH_INITIAL_FORM);

    const { control, handleSubmit, formState } = useForm<RemindPassFields>({
        resolver: remindPassResolver,
        defaultValues: {
            email: initialFormData?.email || searchQuery?.email || '',
        },
    });

    const onSubmit: SubmitHandler<RemindPassFields> = async ({ email }) => {
        try {
            setCommonErrorMessage('');
            await auth.remindPass({ email });
            setEmailForLetter(email);
        } catch (error) {
            const errorMessage = error.reason
                ? error.reason
                : t`Йой! Щось пішло не так.`;
            setCommonErrorMessage(errorMessage);
        }
    };

    const onBackHandler = () => {
        history.push({
            pathname: '/auth/login',
            search: stringifyLocationQuery(searchQuery),
        });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <PageTitle>{t`Відновлення паролю`}</PageTitle>
            <FlexBox className={css.container} justify="center" gap={0}>
                <div className={css.content}>
                    {emailForLetter ? (
                        <FlexBox
                            className={css.header}
                            direction="column"
                            gap={27}
                        >
                            <BackButton
                                className={css.backBtn}
                                onClick={onBackHandler}
                            />
                            <img
                                src={partnersLogo}
                                className={css.partnersLogos}
                                alt=""
                            />
                            <Alert theme="success" hideIcon>
                                {t`Якщо вказаний користувач існує, то на email`}{' '}
                                <b>{emailForLetter}</b>{' '}
                                {t`було надіслано посилання для відновлення пароля.`}
                            </Alert>
                        </FlexBox>
                    ) : (
                        <>
                            <FlexBox
                                className={css.header}
                                direction="column"
                                gap={27}
                            >
                                <BackButton
                                    className={css.backBtn}
                                    onClick={onBackHandler}
                                />
                                <img
                                    src={partnersLogo}
                                    className={css.partnersLogos}
                                    alt=""
                                />
                                <FlexBox direction="column">
                                    <h1
                                        className={css.title}
                                    >{t`Відновлення паролю`}</h1>
                                    <h4
                                        className={css.description}
                                    >{t`Введіть email на який ми надішлемо посилання для відновлення пароля`}</h4>
                                </FlexBox>
                            </FlexBox>
                            <Controller
                                control={control}
                                name="email"
                                render={({ field, fieldState }) => (
                                    <OutlinedInput
                                        type="text"
                                        label={t`Email`}
                                        value={field.value}
                                        onChange={(event) =>
                                            field.onChange(
                                                event.target.value.trim(),
                                            )
                                        }
                                        autoFocus
                                        maxLength={EMAIL_MAX_LENGTH + 1}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                            {commonErrorMessage && (
                                <Alert theme="error" hideIcon>
                                    {commonErrorMessage}
                                </Alert>
                            )}
                            <Button
                                className={css.submitBtn}
                                type="submit"
                                theme="darkGray"
                                disabled={formState.isSubmitting}
                            >{t`Продовжити`}</Button>
                        </>
                    )}
                </div>
            </FlexBox>
        </form>
    );
};

export default NewRemindPassForm;
